import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Capacitor } from '@capacitor/core';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import 'hammerjs';

if (environment.production) {
  enableProdMode();
}

const CHUNK_RECOVERY_KEY = 'chunk_recovery_ts';
const CHUNK_RECOVERY_COOLDOWN_MS = 60_000; // 1 minute cooldown to prevent reload loops
const SW_CLEANUP_RELOAD_KEY = 'sw_cleanup_reload_ts';
const SW_CLEANUP_RELOAD_COOLDOWN_MS = 60_000; // 1 minute cooldown to prevent reload loops

function normalizeError(err: unknown): Error {
  if (err instanceof Error) return err;
  try {
    return new Error(typeof err === 'string' ? err : JSON.stringify(err));
  } catch {
    return new Error(String(err));
  }
}

function hasServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

function hasCaches(): boolean {
  return typeof window !== 'undefined' && 'caches' in window;
}

function isLikelyChunkError(err: Error): boolean {
  const msg = (err?.message || '').toLowerCase();

  // Keep detection strict to avoid treating offline/API errors as chunk issues.
  return (
    msg.includes('chunkloaderror') ||
    msg.includes('loading chunk') ||
    (msg.includes('failed to fetch') && msg.includes('.js')) ||
    (msg.includes('network error') && msg.includes('chunk'))
  );
}

function isCooldownOver(key: string, cooldownMs: number): boolean {
  try {
    const now = Date.now();
    const last = Number(localStorage.getItem(key) || '0');
    if (now - last < cooldownMs) return false;
    localStorage.setItem(key, String(now));
    return true;
  } catch {
    // If localStorage is unavailable, allow recovery once (best effort).
    return true;
  }
}

function shouldRunRecoveryNow(): boolean {
  return isCooldownOver(CHUNK_RECOVERY_KEY, CHUNK_RECOVERY_COOLDOWN_MS);
}

/**
 * App versions before 1.9.10 registered the Angular service worker on native
 * platforms too. Its registration and caches live in the WebView storage and
 * survive native app updates, so the old cached app shell keeps being served
 * until the registration is removed explicitly.
 *
 * Returns true when a reload was triggered (bootstrap should be skipped).
 */
async function removeStaleNativeServiceWorker(): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || !hasServiceWorker()) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) {
      return false;
    }

    console.warn('Stale Service Worker registration found on native platform, removing...');
    const controlled = !!navigator.serviceWorker.controller;
    await resetServiceWorkerCache();

    // unregister() does not release the current page from the old worker, so
    // reload once when this page load was still served by it.
    if (controlled && isCooldownOver(SW_CLEANUP_RELOAD_KEY, SW_CLEANUP_RELOAD_COOLDOWN_MS)) {
      window.location.reload();
      return true;
    }
  } catch (error) {
    console.error('Error removing stale Service Worker:', error);
    // Do not throw - the app should still bootstrap.
  }

  return false;
}

async function resetServiceWorkerCache(): Promise<void> {
  try {
    if (hasServiceWorker()) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }

    if (hasCaches()) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    console.log('Service Worker/cache cleared');
  } catch (error) {
    console.error('Error resetting Service Worker/cache:', error);
    // Do not throw - recovery should be best effort.
  }
}

function renderBootstrapErrorPage(err: Error): void {
  // Render a minimal fallback UI and attach handlers via JS (no inline scripts).
  document.body.innerHTML = `
    <div style="display:flex;justify-content:center;align-items:center;height:100vh;flex-direction:column;padding:20px;text-align:center;font-family:Arial,sans-serif;background:#f5f5f5;">
      <h2 style="color:#d32f2f;margin-bottom:20px;">App-Fehler</h2>
      <p style="margin-bottom:20px;color:#333;">Die App konnte nicht gestartet werden.</p>
      <p style="margin-bottom:30px;font-size:14px;color:#666;">Bitte versuchen Sie die App neu zu laden oder installieren Sie sie neu.</p>
      <div>
        <button id="btnReload" style="padding:12px 24px;background:#1976d2;color:white;border:none;border-radius:4px;cursor:pointer;font-size:16px;margin-right:10px;">
          App neu laden
        </button>
        <button id="btnReset" style="padding:12px 24px;background:#666;color:white;border:none;border-radius:4px;cursor:pointer;font-size:16px;">
          Cache leeren & neu laden
        </button>
      </div>
      <pre style="margin-top:24px;max-width:900px;white-space:pre-wrap;color:#444;font-size:12px;text-align:left;">
${String(err?.message || err)}
      </pre>
    </div>
  `;

  document.getElementById('btnReload')?.addEventListener('click', () => {
    window.location.reload();
  });

  document.getElementById('btnReset')?.addEventListener('click', async () => {
    await resetServiceWorkerCache();
    window.location.reload();
  });
}

async function handleBootstrapError(err: unknown): Promise<void> {
  const error = normalizeError(err);
  console.error('Bootstrap error:', error);

  if (isLikelyChunkError(error) && shouldRunRecoveryNow()) {
    console.log('Detected chunk loading error, resetting cache...');
    await resetServiceWorkerCache();

    // Reload after a short delay to allow unregister/delete to settle.
    setTimeout(() => {
      window.location.reload();
    }, 1000);

    return;
  }

  renderBootstrapErrorPage(error);
}

async function bootstrap(): Promise<void> {
  const reloading = await removeStaleNativeServiceWorker();
  if (reloading) {
    return;
  }

  await platformBrowserDynamic().bootstrapModule(AppModule);
}

bootstrap().catch(handleBootstrapError);